/**
 * "Open Bird, here's what to ask it" — from any surface to the Bird panel.
 *
 * Layout owns the panel and listens for this event; every entry point (Cmd-K
 * Ask Bird, the dashboard composer, the per-surface Ask-Bird CTAs) dispatches
 * it. Dispatch through `openBirdChat` rather than hand-writing the event: a
 * typo in a `dispatchEvent` name is silent, and — more importantly — the
 * fresh-context DEFAULT below lives here, so a new entry point gets it without
 * knowing it exists.
 *
 * ── The fresh-context default ─────────────────────────────────────────
 * A handover that carries a PROMPT starts a NEW conversation; a handover that
 * merely opens the panel resumes the existing one.
 *
 * Rationale: a prompt handed over from another surface is a new topic, and the
 * user can't see what it is about to be appended to. Before this default,
 * "Ask Bird about X" from the search palette landed in whatever thread happened
 * to be open — so a question about capturing voice notes was answered in the
 * context of a long thread about moving DNA papers between collections. A bare
 * open is the opposite intent: the user is going back to their conversation.
 *
 * Callers may override with an explicit `freshContext`, and the prompt-less
 * Ask-Bird CTAs do (they start a new topic despite carrying no text).
 */

// The types module, NOT the component — importing ChatComposer.tsx here closes
// a 9-module cycle that fails `pnpm deps:circular`. See ChatComposer.types.ts.
import type { ChatComposerPayload } from '../../components/features/bird/ChatComposer.types';
import type { ChatContext } from '../../contexts/ChatContextProvider';

const EVENT_NAME = 'chat:openPanel';

/** What the panel is being asked to do. `source` is analytics-only, so it
 *  isn't part of this. Callers pass at most one of `initialMessage` / `send`;
 *  if both arrive (a raw dispatch), `send` wins and the pre-fill is ignored —
 *  pre-filling too would strand text in the composer looking unsent. */
export interface BirdChatHandover {
  /** Pre-fill the composer — the user still presses send. */
  readonly initialMessage?: string;
  /** Exact Bower entities that stay attached to an editable pre-fill. */
  readonly initialContexts?: readonly ChatContext[];
  /** Full payload to SEND once the panel is ready (dashboard hero). */
  readonly send?: ChatComposerPayload;
  /** Start a new session before prefilling/sending. Resolved, never optional. */
  readonly freshContext: boolean;
  /**
   * Open THIS existing conversation instead of whatever is currently open.
   * Set by chat-history search, which knows exactly which thread it matched.
   */
  readonly sessionId?: string;
  /**
   * Scroll to (and briefly highlight) this message once history has loaded.
   * The ADK event id from `HistoryMessage.message_id`. Only meaningful
   * alongside `sessionId`.
   */
  readonly messageId?: string;
}

/** The wire shape on the window bus. Every field is optional because a raw
 *  `dispatchEvent` (a test, older code) can omit any of them. */
export interface BirdChatOpenDetail {
  readonly source?: string;
  readonly initialMessage?: string;
  readonly initialContexts?: readonly ChatContext[];
  readonly send?: ChatComposerPayload;
  readonly freshContext?: boolean;
  readonly sessionId?: string;
  readonly messageId?: string;
}

export interface OpenBirdChatOptions {
  /** Analytics dimension on BIRD_CHAT_OPENED — a dashboard contract. */
  readonly source: string;
  readonly initialMessage?: string;
  readonly initialContexts?: readonly ChatContext[];
  readonly send?: ChatComposerPayload;
  /** Open a specific existing conversation (chat-history search). */
  readonly sessionId?: string;
  /** Scroll to this message inside `sessionId` once its history loads. */
  readonly messageId?: string;
  /**
   * Overrides the prompt-implies-fresh default documented above.
   *
   * `false` TOGETHER WITH `send` is the one combination no caller currently
   * produces, and the only one that can park in the panel for a long time (a
   * resume send waits out an in-flight stream, whereas a fresh send's own
   * reset clears streaming within a render). While parked, a bare open
   * REPLACES it and the user's composed message is discarded. Before adding a
   * "send into the open thread" entry point, make that window safe — have
   * Layout refuse to replace an unconsumed send handover, or queue it.
   */
  readonly freshContext?: boolean;
}

/**
 * The `/m/bird` navigation-state contract: on mobile Bird is a full-page route,
 * so Layout hands the panel its handover through router state instead of a prop
 * (a `send` payload degrades to editable text; exact entity contexts remain
 * serializable, while File objects do not ride history).
 *
 * Declared once, HERE, because the writer (Layout) and the reader
 * (MobileBirdPage) sit on opposite sides of a `location.state` cast that the
 * compiler cannot check. Hand-declaring the shape at both ends is the CR-003
 * drift shape: a field added to the writer would compile, pass both suites, and
 * silently never arrive. Readers must treat it as `Partial` — a history entry
 * from before this shipped, or foreign state, carries neither field.
 */
export interface BirdChatNavState {
  readonly initialMessage?: string;
  readonly contexts?: readonly ChatContext[];
  /** Consume-once lookup for a full in-memory send payload (including File objects). */
  readonly handoverToken?: string;
  readonly freshContext: boolean;
  /**
   * Which existing conversation to open, and where to scroll inside it. Both
   * are plain strings, so unlike a `send` payload they ride history state
   * directly and need no in-memory token.
   */
  readonly sessionId?: string;
  readonly messageId?: string;
}

let nextMobileHandoverId = 0;
const mobileHandovers = new Map<string, BirdChatHandover>();
const MAX_PENDING_MOBILE_HANDOVERS = 5;

/** What Layout gets: the analytics source plus a resolved handover. */
export interface BirdChatOpenRequest {
  readonly source: string;
  readonly handover: BirdChatHandover;
}

/**
 * The one place the default is decided. Called on BOTH sides — by
 * `openBirdChat` so the dispatched event states its intent explicitly, and by
 * the subscriber so a raw `dispatchEvent` that skipped the helper still gets
 * the right behaviour. Applying it twice is idempotent.
 */
export function resolveBirdChatHandover(detail: BirdChatOpenDetail): BirdChatHandover {
  const carriesPrompt = !!detail.send || !!detail.initialMessage;
  return {
    initialMessage: detail.initialMessage,
    initialContexts: detail.initialContexts,
    send: detail.send,
    // Targeting an existing conversation is the OPPOSITE of a fresh context:
    // search matched a specific thread, so resuming it is the entire point.
    // Without this clause a future caller combining a prompt with a sessionId
    // would silently start a new session and discard the target.
    freshContext: detail.sessionId ? false : (detail.freshContext ?? carriesPrompt),
    sessionId: detail.sessionId,
    messageId: detail.messageId,
  };
}

export function openBirdChat({
  source,
  initialMessage,
  initialContexts,
  send,
  freshContext,
  sessionId,
  messageId,
}: OpenBirdChatOptions): void {
  if (typeof window === 'undefined') return;
  // Absent fields are omitted rather than set to undefined — the detail is what
  // a listener (and a test) reads to see what was asked for, so it shouldn't
  // carry keys the caller never set.
  const detail: BirdChatOpenDetail = {
    source,
    ...(initialMessage === undefined ? {} : { initialMessage }),
    ...(initialContexts === undefined ? {} : { initialContexts }),
    ...(send === undefined ? {} : { send }),
    ...(sessionId === undefined ? {} : { sessionId }),
    ...(messageId === undefined ? {} : { messageId }),
    freshContext: resolveBirdChatHandover({ initialMessage, send, freshContext, sessionId })
      .freshContext,
  };
  window.dispatchEvent(new CustomEvent<BirdChatOpenDetail>(EVENT_NAME, { detail }));
}

export function birdChatNavStateForHandover(handover: BirdChatHandover): BirdChatNavState {
  const serializableState: BirdChatNavState = {
    initialMessage: handover.send?.text ?? handover.initialMessage,
    contexts: handover.send?.contexts ?? handover.initialContexts,
    freshContext: handover.freshContext,
    // Carried BEFORE the early return below. A chat-search handover has no
    // `send`, so it takes that return — which is exactly how the target
    // conversation got dropped on mobile while compiling and passing every
    // suite (the drift this interface's own doc comment warns about).
    sessionId: handover.sessionId,
    messageId: handover.messageId,
  };
  if (!handover.send) return serializableState;

  nextMobileHandoverId += 1;
  const handoverToken = `mobile-bird-${nextMobileHandoverId}`;
  mobileHandovers.set(handoverToken, handover);
  while (mobileHandovers.size > MAX_PENDING_MOBILE_HANDOVERS) {
    const oldest = mobileHandovers.keys().next().value as string | undefined;
    if (!oldest) break;
    mobileHandovers.delete(oldest);
  }
  return { ...serializableState, handoverToken };
}

export function readMobileBirdHandover(token: string | undefined): BirdChatHandover | null {
  return token ? (mobileHandovers.get(token) ?? null) : null;
}

export function clearMobileBirdHandover(token: string | undefined): void {
  if (token) mobileHandovers.delete(token);
}

/**
 * Subscribes to open-Bird requests. Returns a cleanup function suitable for a
 * React `useEffect` return.
 */
export function subscribeBirdChatOpen(handler: (request: BirdChatOpenRequest) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event) => {
    const detail = (event as CustomEvent<BirdChatOpenDetail>).detail ?? {};
    handler({
      source: detail.source ?? 'unknown',
      handover: resolveBirdChatHandover(detail),
    });
  };
  window.addEventListener(EVENT_NAME, listener);
  return () => window.removeEventListener(EVENT_NAME, listener);
}
